import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaTrash, FaBuilding } from 'react-icons/fa';
import Swal from 'sweetalert2';
import DataTable from '../../../../components_shared/DataTable';
import TableToolbar from '../../../../components_shared/TableToolbar';
import { useApp } from '../../../../context/AppContext';
import { publisherAdminAPI as publisherAPI } from '../../../../utils/api';
import PublisherCreate from '../../create/js/publisherCreate';
import PropTypes from 'prop-types';

function PublisherTable({ onCreatePublisher }) {
  const { permissions, roles: userRoles } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [editModal, setEditModal] = useState(false);

  const isSuperAdmin = userRoles && (userRoles.includes('SUPER_ADMIN') || userRoles.includes('ROLE_SUPER_ADMIN'));

  useEffect(() => {
    fetchPublishers();
  }, []);

  const fetchPublishers = async () => {
    setLoading(true);
    try {
      const res = await publisherAPI.getAll();
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      Swal.fire({ title: 'Error', text: err.message, icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const remove = async (publisher) => {
    const res = await Swal.fire({
      title: 'Delete Publisher?',
      text: `Are you sure you want to delete ${publisher.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonColor: '#e74c3c',
    });
    if (res.isConfirmed) {
      try {
        await publisherAPI.delete(publisher.id);
        await fetchPublishers();
        Swal.fire({ title: 'Deleted', text: 'Publisher removed', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
      } catch (err) { Swal.fire('Error', err.message, 'error'); }
    }
  };

  const exportCsv = async () => {
    try {
      const res = await publisherAPI.exportCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'publishers_export.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { Swal.fire('Error', err.message, 'error'); }
  };

  const importCsv = async (csv) => {
    const res = await publisherAPI.importCsv(csv);
    await fetchPublishers();
    return res.data;
  };

  const downloadSample = async () => {
    try {
      const res = await publisherAPI.sampleCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'publishers_sample.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { 
      accessorKey: 'name', 
      header: 'Publisher Name', 
      cell: ({ getValue }) => <span style={{ fontWeight: 600, color: '#2d3436' }}>{getValue()}</span> 
    },
    { accessorKey: 'contact_name', header: 'Contact Person' },
    { accessorKey: 'phone', header: 'Phone' },
    { accessorKey: 'email', header: 'Email' },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn view" onClick={() => { setSelected(row.original); setViewModal(true); }}><FaEye /></button>
          {(permissions.includes('EDIT_PUBLISHER') || isSuperAdmin) && (
            <button className="eduss-global-action-btn edit" onClick={() => { setSelected(row.original); setEditModal(true); }}><FaEdit /></button>
          )}
          {(permissions.includes('DELETE_PUBLISHER') || isSuperAdmin) && (
            <button className="eduss-global-action-btn delete" onClick={() => remove(row.original)}><FaTrash /></button>
          )}
        </div>
      ),
    },
  ];

  const filteredData = data.filter(p => 
    !filter || 
    p.name?.toLowerCase().includes(filter.toLowerCase()) ||
    p.contact_name?.toLowerCase().includes(filter.toLowerCase()) ||
    p.email?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar 
        title="Publisher Registry"
        subtitle="Manage licensed publishers and their corporate credentials"
        onAdd={(permissions.includes('CREATE_PUBLISHER') || isSuperAdmin) ? onCreatePublisher : null}
        onExport={exportCsv}
        onImport={importCsv}
        onDownloadSample={downloadSample}
        onSearch={setFilter}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
          enableSelection={true}
        />
      </div>

      {viewModal && selected && (
        <div className="eduss-global-modal-overlay" onClick={() => setViewModal(false)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Publisher Profile</h3>
              <button onClick={() => setViewModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <div style={{ display: 'flex', gap: 20, marginBottom: 25, alignItems: 'center' }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(15, 111, 173, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: '#0f6fad' }}>
                  <FaBuilding />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1.4rem' }}>{selected.name}</h4>
                  <p style={{ margin: 0, color: '#666' }}>Authorized Publisher</p>
                </div>
              </div>
              <table className="mini-info-table">
                <tbody>
                  <tr><th>Contact Person</th><td>{selected.contact_name}</td></tr>
                  <tr><th>Phone Number</th><td>{selected.phone}</td></tr>
                  <tr><th>Email Address</th><td>{selected.email}</td></tr>
                  <tr><th>Created Date</th><td>{selected.created_at || '-'}</td></tr>
                  <tr><th>Last Updated</th><td>{selected.updated_at || '-'}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {editModal && selected && (
        <div className="eduss-global-modal-overlay" onClick={() => setEditModal(false)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Update Publisher</h3>
              <button onClick={() => setEditModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <PublisherCreate publisher={selected} onClose={() => setEditModal(false)} onSubmit={() => fetchPublishers()} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

PublisherTable.propTypes = { onCreatePublisher: PropTypes.func };
export default PublisherTable;
